import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { UpdateSensorRequestSchema, type UpdateSensorRequest } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/notify";
import { isApiClientError } from "@/lib/api-client";
import { useUpdateSensor, type SensorSummary } from "@/features/sensors/hooks";

interface SensorEditFormProps {
  deviceId: string;
  sensor: SensorSummary;
  onDirtyChange: (dirty: boolean) => void;
}

// Apenas code/name/dataType são editáveis; position e escala são imutáveis
// (exibidos como leitura no painel).
export function SensorEditForm({ deviceId, sensor, onDirtyChange }: SensorEditFormProps) {
  const update = useUpdateSensor(deviceId);
  const form = useForm<UpdateSensorRequest>({
    resolver: zodResolver(UpdateSensorRequestSchema),
    defaultValues: { code: sensor.code, name: sensor.name, dataType: sensor.dataType },
  });
  const dirty = form.formState.isDirty;
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  async function onSubmit(values: UpdateSensorRequest) {
    try {
      await update.mutateAsync({ id: sensor.id, data: values });
      toast.success("Sensor atualizado.");
      form.reset(values);
    } catch (err) {
      toast.error("Falha ao salvar.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  const errs = form.formState.errors;
  return (
    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="sensor-code">Código do sensor</Label>
          <Input id="sensor-code" {...form.register("code")} />
          {errs.code && <p className="text-xs text-destructive">{errs.code.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sensor-name">Nome</Label>
          <Input id="sensor-name" {...form.register("name")} />
          {errs.name && <p className="text-xs text-destructive">{errs.name.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sensor-dtype">Tipo de dado</Label>
          <Input id="sensor-dtype" {...form.register("dataType")} />
          {errs.dataType && <p className="text-xs text-destructive">{errs.dataType.message}</p>}
        </div>
      </div>
      <Button type="submit" size="sm" disabled={!dirty || update.isPending}>
        {update.isPending ? "Salvando…" : "Salvar"}
      </Button>
    </form>
  );
}
